/*
 * Copyright (c) 2025 Autire Technologies, LLC. All Rights Reserved.
 *
 * This product and related documentation is protected by copyright and
 * distributed under licenses restricting its use, copying, distribution and
 * decompilation. No part of this product or related documentation may be
 * reproduced in any form by any means without prior written authorization
 * from Autire Technologies, LLC and/or its licensors.
 */

import React from 'react';
/**
 * @fileoverview Utility functions for password generation and URL parameter encoding/decoding
 * @description Contains helper functions for generating secure passwords and encoding/decoding
 * URL parameters using Base64. These utilities are used throughout the application
 * for security and data transmission purposes. All functions include comprehensive
 * error handling and are designed for production use.
 *
 * @module UtilityFunctions
 * @author PKP Technologies, LLC
 * @since 1.0.0
 * @version 1.0.0
 *
 * @example
 * import { genPassword, encodeQueryParamToBase64, decodeQueryParamToBase64 } from './utility';
 *
 * // Generate secure password
 * const password = genPassword();
 *
 * // Encode URL parameter
 * const encoded = encodeQueryParamToBase64('user@example.com');
 *
 * // Decode URL parameter
 * const decoded = decodeQueryParamToBase64(encoded);
 *
 * @see {@link encryption.js} For encryption/decryption functions
 * @see {@link validation.js} For input validation functions
 */

export { genPassword, encodeQueryParamToBase64, decodeQueryParamToBase64, isDevUser };

/**
 * Checks if the current user is a dev/internal user
 *
 * @description Determines if the logged-in user is a development or internal user
 * by checking the custom:logLevel attribute in localStorage. Returns true if the
 * user's logLevel is 9, indicating dev/internal access. This function is used
 * throughout the application to control access to development features and tools.
 *
 * @function isDevUser
 * @returns {boolean} True if user is a dev user (logLevel === 9), false otherwise
 *
 * @example
 * // Basic usage
 * if (isDevUser()) {
 *   console.log('User has dev access');
 * }
 *
 * @example
 * // Conditional feature access
 * const showDevTools = isDevUser();
 * const cpaList = allCpas.filter(cpa =>
 *   isDevUser() ? true : cpa.cpaId !== 'DevOps'
 * );
 *
 * @since 1.0.0
 * @author Autire Technologies, LLC
 */
function isDevUser() {
  try {
    const payload = localStorage.getItem('payload');
    if (!payload) {
      return false;
    }
    const user = JSON.parse(payload);
    return user && user['custom:logLevel'] === '9';
  } catch (error) {
    console.error('Error checking dev user status:', error);
    return false;
  }
}

/**
 * Generates a secure password with specific character requirements
 *
 * @description Creates a 16-character password that includes at least one character
 * from each category: numbers (0-9), lowercase letters (a-z), uppercase letters (A-Z),
 * and special characters (!@#$%^&*()). Uses a deterministic algorithm to ensure
 * password strength requirements are met. The function iterates through each character
 * category and selects random characters to build a strong password.
 *
 * @function genPassword
 * @returns {string} A 16-character password with mixed character types
 *
 * @example
 * // Basic usage
 * const password = genPassword();
 * console.log(password); // "Kj9#mN2$pQ5@vX8!"
 *
 * @example
 * // Validate password strength
 * const password = genPassword();
 * const hasNumber = /\d/.test(password);
 * const hasLower = /[a-z]/.test(password);
 * const hasUpper = /[A-Z]/.test(password);
 * const hasSpecial = /[!@#$%^&*()]/.test(password);
 *
 * console.log('Password strength:', {
 *   hasNumber, hasLower, hasUpper, hasSpecial
 * });
 * // Output: { hasNumber: true, hasLower: true, hasUpper: true, hasSpecial: true }
 *
 * @example
 * // Generate multiple passwords
 * const passwords = Array.from({ length: 3 }, () => genPassword());
 * console.log('Generated passwords:', passwords);
 *
 * @throws {Error} When password generation fails (rare, but possible due to random number generation issues)
 *
 * @since 1.0.0
 * @author PKP Technologies, LLC
 * @see {@link validatePassword} For password validation
 * @see {@link encrypt} For password encryption
 */
function genPassword() {
  let chars = [
    '0123456789',
    'abcdefghijklmnopqrstuvwxyz',
    '!@#$%^&*()',
    'ABCDEFGHIJKLMNOPQRSTUVWXYZ',
  ];
  let passwordLength = 4;
  let password = '';
  for (let k of chars) {
    for (let i = 0; i <= passwordLength; i++) {
      const crypto = globalThis.crypto || globalThis.msCrypto;
      let array = new Uint32Array(1);
      // Normalize the random value to a decimal between 0 and 1, then multiply by string length
      let randomValue = crypto.getRandomValues(array)[0] / (0xffffffff + 1);
      let randomNumber = Math.floor(randomValue * k.length);
      password += k.substring(randomNumber, randomNumber + 1);
    }
  }
  return password;
}

/**
 * Encodes URL query parameters to Base64 format
 *
 * @description Converts URL query parameters to Base64 encoding for secure transmission.
 * Handles special characters and ensures proper URL encoding before Base64 conversion.
 * Returns null if encoding fails due to invalid input or encoding errors.
 * This function is commonly used for encoding sensitive data in URLs or API requests.
 *
 * @function encodeQueryParamToBase64
 * @param {string} param - The query parameter to encode
 * @returns {string|null} Base64 encoded parameter or null if encoding fails
 *
 * @example
 * // Basic encoding
 * const encoded = encodeQueryParamToBase64('user@example.com');
 * console.log(encoded); // "dXNlckBleGFtcGxlLmNvbQ=="
 *
 * @example
 * // Encoding with special characters
 * const encoded = encodeQueryParamToBase64('search term with spaces');
 * console.log(encoded); // "c2VhcmNoJTIwdGVybSUyMHdpdGglMjBzcGFjZXM="
 *
 * @example
 * // Encoding for URL parameters
 * const userId = 'user123';
 * const encodedUserId = encodeQueryParamToBase64(userId);
 * const url = `https://api.example.com/user/${encodedUserId}`;
 *
 * @example
 * // Error handling
 * const encoded = encodeQueryParamToBase64(null);
 * console.log(encoded); // null
 *
 * @throws {Error} When encoding process fails (logged to console)
 *
 * @since 1.0.0
 * @author PKP Technologies, LLC
 * @see {@link decodeQueryParamToBase64} For decoding function
 * @see {@link encrypt} For encryption function
 */
function encodeQueryParamToBase64(param) {
  try {
    return btoa(encodeURIComponent(param));
  } catch (error) {
    console.error('An error occurred while encoding:', error);
    return null;
  }
}

/**
 * Decodes Base64 encoded URL query parameters
 *
 * @description Converts Base64 encoded parameters back to their original string format.
 * Handles URL decoding after Base64 decoding to restore special characters.
 * Returns null if decoding fails due to invalid Base64 string or decoding errors.
 * This function is the inverse of encodeQueryParamToBase64 and is used to decode
 * parameters received from URLs or API responses.
 *
 * @function decodeQueryParamToBase64
 * @param {string} encodedParam - The Base64 encoded parameter to decode
 * @returns {string|null} Decoded parameter or null if decoding fails
 *
 * @example
 * // Basic decoding
 * const encoded = 'dXNlckBleGFtcGxlLmNvbQ==';
 * const decoded = decodeQueryParamToBase64(encoded);
 * console.log(decoded); // "user@example.com"
 *
 * @example
 * // Decoding with special characters
 * const encoded = 'c2VhcmNoJTIwdGVybSUyMHdpdGglMjBzcGFjZXM=';
 * const decoded = decodeQueryParamToBase64(encoded);
 * console.log(decoded); // "search term with spaces"
 *
 * @example
 * // Decoding from URL
 * const url = 'https://api.example.com/user/dXNlckBleGFtcGxlLmNvbQ==';
 * const encodedUserId = url.split('/').pop();
 * const userId = decodeQueryParamToBase64(encodedUserId);
 * console.log(userId); // "user@example.com"
 *
 * @example
 * // Error handling
 * const decoded = decodeQueryParamToBase64('invalid-base64-string!');
 * console.log(decoded); // null
 *
 * @throws {Error} When decoding process fails (logged to console)
 *
 * @since 1.0.0
 * @author PKP Technologies, LLC
 * @see {@link encodeQueryParamToBase64} For encoding function
 * @see {@link decrypt} For decryption function
 */
function decodeQueryParamToBase64(encodedParam) {
  try {
    return atob(decodeURIComponent(encodedParam));
  } catch (error) {
    if (error instanceof DOMException && error.name === 'InvalidCharacterError') {
      console.error('Invalid base64 string:', error.message);
    } else {
      console.error('An error occurred while decoding:', error);
    }
    return null;
  }
}

import { useLocation, useNavigate, useParams } from 'react-router-dom';

export function withRouter(Component) {
  function ComponentWithRouterProp(props) {
    const location = useLocation();
    const navigate = useNavigate();
    const params = useParams();

    return <Component {...props} location={location} navigate={navigate} params={params} />;
  }

  return ComponentWithRouterProp;
}
